import { apiGet, apiPatch, apiPost } from "./client";

export type ConversionRecipeRow = {
  id: string;
  fromProductId: string;
  toProductId: string;
  fromName: string;
  toName: string;
  factor: string;
  name: string | null;
  deactivatedAt: string | null;
};

export function listConversionRecipes(
  companyRef: string,
  opts?: { includeDeactivated?: boolean },
) {
  const q = opts?.includeDeactivated ? "?all=1" : "";
  return apiGet<{ recipes: ConversionRecipeRow[] }>(
    `/c/${companyRef}/inventory/conversion-recipes${q}`,
  );
}

export function createConversionRecipe(
  companyRef: string,
  body: {
    fromProductId: string;
    toProductId: string;
    factor: string;
    name?: string;
  },
) {
  return apiPost<{
    recipe: {
      id: string;
      fromProductId: string;
      toProductId: string;
      factor: string;
      reactivated?: boolean;
    };
  }>(`/c/${companyRef}/inventory/conversion-recipes`, body);
}

export function updateConversionRecipe(
  companyRef: string,
  recipeId: string,
  body: { name: string | null },
) {
  return apiPatch<{ recipe: { id: string; name: string | null } }>(
    `/c/${companyRef}/inventory/conversion-recipes/${recipeId}`,
    body,
  );
}

export function deactivateConversionRecipe(
  companyRef: string,
  recipeId: string,
) {
  return apiPost<{ recipe: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/inventory/conversion-recipes/${recipeId}/deactivate`,
  );
}

export function reactivateConversionRecipe(
  companyRef: string,
  recipeId: string,
) {
  return apiPost<{ recipe: { id: string; deactivated: boolean } }>(
    `/c/${companyRef}/inventory/conversion-recipes/${recipeId}/reactivate`,
  );
}

export function postConversion(
  companyRef: string,
  body: {
    recipeId?: string;
    fromProductId?: string;
    toProductId?: string;
    factor?: string;
    qtyFrom: string;
    warehouseId?: string;
    confirmOversell?: boolean;
    fromLotCode?: string;
    fromSerialCode?: string;
    toLotCode?: string;
    toSerialCode?: string;
    idempotencyKey?: string;
  },
) {
  return apiPost<{
    conversion: {
      id: string;
      number: string;
      qtyFrom: string;
      qtyTo: string;
      unitCostFrom: string;
      unitCostTo: string;
      oversell: boolean;
    };
    replayed?: boolean;
  }>(`/c/${companyRef}/inventory/conversions`, body);
}

export type ConversionRow = {
  id: string;
  number: string;
  fromProductId: string;
  toProductId: string;
  qtyFrom: string;
  qtyTo: string;
  postedAt: string | null;
  createdAt: string;
};

export type ConversionListPage = {
  conversions: ConversionRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type ConversionListSort = "number" | "createdAt";

export function listConversions(
  companyRef: string,
  opts?: {
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: ConversionListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<ConversionListPage>(
    `/c/${companyRef}/inventory/conversions${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function getConversion(companyRef: string, conversionId: string) {
  return apiGet<{
    conversion: {
      id: string;
      number: string;
      lifecycle: string;
      qtyFrom: string;
      qtyTo: string;
      unitCostFrom: string;
      unitCostTo: string;
      notes: string | null;
      postedAt: string | null;
      fromProductId: string;
      toProductId: string;
      fromName: string;
      toName: string;
      warehouseId: string;
      warehouseName: string | null;
      warehouseCode: string | null;
      productConversionId: string | null;
    };
  }>(`/c/${companyRef}/inventory/conversions/${conversionId}`);
}
